import isEmpty from 'lodash/isEmpty';
import { createModel } from '@rematch/core';

import { AuthUser, OrgInfo } from '@libraries/api/gen-api';

import { AllRoles } from '../../types/types';

import {
  ORG_AUDITOR,
  ORG_DISTRIBUTOR,
  ORG_OWNER,
  ORG_RESELLER,
  SERVICE_AUDITOR,
  SERVICE_OWNER,
} from '../constants/roles';
import { AuthenticationMethod, isTestEnv, sortRoles } from '../helpers';

import { api } from '../api';
import { getDefaultRole, removeDefaultRoleFromStorage, setDefaultRoleToStorage } from '../utils';
import type { RootModel } from '.';

export interface AuthStateType {
  permissions?: Array<string>;
  me?: AuthUser;
  activeView?: string;
}

export const authInitialState: AuthStateType = {
  permissions: [],
  me: {},
  activeView: '',
};

export const auth = createModel<RootModel>()({
  state: { ...authInitialState },
  /* eslint-disable @typescript-eslint/no-unsafe-member-access  */
  selectors: (slice, createSelector) => ({
    // creates a simple memoized selector

    isServiceUser() {
      return slice((state) => state.activeView?.startsWith('service-'));
    },
    isServiceOwner() {
      return slice((state) => state.activeView === SERVICE_OWNER);
    },
    isOrgOwner() {
      return slice((state) => state.activeView === ORG_OWNER);
    },
    isAuditor() {
      return slice((state) => state.activeView === ORG_AUDITOR || state.activeView === SERVICE_AUDITOR);
    },
    isReseller() {
      return slice((state) => state.activeView === ORG_RESELLER);
    },
    isDistributor() {
      return slice((state) => state.activeView === ORG_DISTRIBUTOR);
    },
    getOrg() {
      return slice((state) => state.me?.org);
    },
    getOrgs() {
      return slice((state) => state.me?.orgs);
    },
    isPasswordlessMigrated() {
      return slice((state) => state.me?.passwordless_migrated);
    },
    isSSOLoggedIn() {
      return slice((state) => state.me?.authentication_method === AuthenticationMethod.SSO);
    },
    isDemoSession() {
      return slice((state) => state.me?.authentication_method === AuthenticationMethod.DEMO);
    },
    hasNoBackupPasskey() {
      return slice(
        (state) =>
          state.me?.authentication_method === AuthenticationMethod.PASSWORDLESS &&
          state.me?.passkey_count === 1
      );
    },
    isMFARegistered() {
      return slice((state) => state.me?.mfa_registered);
    },

    isDemoUser() {
      return createSelector(this.isDemoSession, (isDemoSession: boolean) => isDemoSession);
    },

    showDemoAlert() {
      return createSelector(
        this.isDemoSession,
        this.isMFARegistered,
        (isDemoSession: boolean, isMFARegistered: boolean) =>
          isTestEnv ? false : isDemoSession && isMFARegistered
      );
    },

    isIAPAuthMethod() {
      return slice((state): boolean => state.me?.authentication_method === AuthenticationMethod.IAP);
    },

    getOrgInfo() {
      return createSelector(
        this.getOrg,
        this.getOrgs,
        (id: string, orgs: { [key: string]: OrgInfo }): OrgInfo => orgs?.[id]
      );
    },

    isSSOEnabled() {
      return createSelector(
        this.getOrgInfo,
        // change value to false or undefiend for testing purposes
        (o: OrgInfo) => o?.sso_enabled === true
      );
    },

    isTabDisabled() {
      return createSelector(
        this.isSSOLoggedIn,
        this.isSSOEnabled,
        (isSSOLoggedIn: boolean, isSSOEnabled: boolean) => !isSSOLoggedIn && isSSOEnabled
      );
    },
    showSSOAlert() {
      return createSelector(
        this.isDemoUser,
        this.isDemoSession,
        (isDemoUser: boolean, isDemoSession: boolean) => !isDemoUser && isDemoSession
      );
    },
    showSSOLoginHelper() {
      return createSelector(
        this.isOrgOwner,
        this.isSSOEnabled,
        this.isSSOLoggedIn,
        (isOrgOwner: boolean, isSSOEnabled: boolean, isSSOLoggedIn: boolean) =>
          isSSOEnabled && !isOrgOwner && !isSSOLoggedIn
      );
    },
    getOrgRoles() {
      return createSelector(
        this.getOrgInfo,
        // Return org roles
        (o: OrgInfo) => sortRoles(o?.roles || [])
      );
    },
  }),
  /* eslint-enable @typescript-eslint/no-unsafe-member-access  */

  reducers: {
    logout: () => ({
      ...authInitialState,
    }),
    user: (state: AuthStateType, payload: AuthUser) => ({
      ...state,
      me: payload,
    }),
    activeView: (state: AuthStateType, payload: string) => ({
      ...state,
      activeView: payload ?? '',
    }),
    permissions: (state: AuthStateType, payload: Array<string>) => ({
      ...state,
      permissions: payload ?? [],
    }),
  },
  effects: (dispatch) => ({
    async userLogout() {
      try {
        await api.AuthApi.logout();
      } finally {
        sessionStorage.clear();
        // reset role first
        dispatch.auth.selectUserRole('');
        dispatch({ type: 'ResetApp' });
      }
    },

    async getPermissions() {
      const res = await api.PermissionsApi.getPermissions();

      if (res.status === 200) {
        dispatch.auth.permissions(res.data?.permissions);
      }
    },

    async getUserInfo() {
      const res = await api.AuthApi.authMeGet();

      if (res.status === 200) {
        const user = res.data;
        dispatch.auth.user(user);
        const { org } = user;
        if (!isEmpty(org)) {
          const activeView = getDefaultRole(res.data);
          dispatch.auth.selectUserRole(activeView);
        }
      }
    },

    async acceptTerms() {
      const res = await api.AuthApi.authMeAcceptTermsPost();
      if (res.status === 200) {
        await dispatch.auth.getUserInfo();
      }
    },

    async selectOrg(org: string) {
      const res = await api.AuthApi.authSetActiveOrgPost({ org_id: org });

      dispatch({ type: 'ResetApp' });

      if (res.status === 200) {
        await dispatch.auth.getUserInfo();
      }
    },
    selectUserRole(role: string, rootState) {
      const { auth } = rootState;
      if (role === '') {
        removeDefaultRoleFromStorage(auth?.me);
      } else {
        setDefaultRoleToStorage(auth?.me, role as AllRoles);
      }
      dispatch.auth.activeView(role);
    },
  }),
});
